/**
 * Utility functions for game-related operations
 */

/**
 * Checks if a game has started based on its start time
 * @param gameTime - The game start time as a string
 * @returns true if the game has started, false otherwise
 */
export const isGameStarted = (gameTime: string): boolean => {
  try {
    const gameStartTime = new Date(gameTime);
    const now = new Date();
    return now >= gameStartTime;
  } catch (error) {
    console.error("Error parsing game time:", gameTime, error);
    return false;
  }
};

/**
 * Sorts games with the following priority:
 * 1. Games that have started but have not yet finished (in progress)
 * 2. Games that have not yet started (upcoming)
 * 3. Games that have finished (completed)
 * @param games - Array of games to sort
 * @param getGameTime - Function to extract the game time from each game object
 * @param getGameCompleted - Function to check if a game is completed
 * @returns Sorted array of games
 */
export const sortGamesByStartStatus = <T>(
  games: T[],
  getGameTime: (game: T) => string,
  getGameCompleted: (game: T) => boolean
): T[] => {
  return [...games].sort((a, b) => {
    const aStarted = isGameStarted(getGameTime(a));
    const bStarted = isGameStarted(getGameTime(b));
    const aCompleted = getGameCompleted(a);
    const bCompleted = getGameCompleted(b);

    // Determine game status for each game
    const getGameStatus = (started: boolean, completed: boolean) => {
      if (started && !completed) return 0; // In progress (highest priority)
      if (!started) return 1; // Not started (medium priority)
      if (completed) return 2; // Finished (lowest priority)
      return 3; // Fallback
    };

    const aStatus = getGameStatus(aStarted, aCompleted);
    const bStatus = getGameStatus(bStarted, bCompleted);

    // If different statuses, sort by status priority
    if (aStatus !== bStatus) {
      return aStatus - bStatus;
    }

    // If same status, sort by time
    return (
      new Date(getGameTime(a)).getTime() - new Date(getGameTime(b)).getTime()
    );
  });
};
